import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, HashRouter } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { useEffect, useState } from "react";
import Index from "@/pages/Index";
import SelectCompany from "@/pages/SelectCompany";
import UserDetails from "./pages/UserDetails";
import UploadResume from "@/pages/UploadResume";
import Interview from "@/pages/Interview";
import NotFound from "@/pages/NotFound";

const queryClient = new QueryClient();

// Component to manage microphone access across route changes
function MicrophoneGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  
  useEffect(() => {
    // Check if we're not on the interview page
    if (!location.pathname.includes('/interview')) {
      // Get all active media streams and stop them
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
          // Close any existing audio tracks
          stream.getTracks().forEach(track => track.stop());
          
          // Get all existing streams and close them
          const mediaDevices = navigator.mediaDevices as any;
          if (mediaDevices.getTracks) {
            const allTracks = mediaDevices.getTracks();
            if (allTracks) {
              allTracks.forEach((track: MediaStreamTrack) => track.stop());
            }
          }
        })
        .catch(err => {
          // Silently catch errors - this may happen if permissions weren't granted
          console.log("No active microphone to turn off");
        });
    }
  }, [location.pathname]);

  return <>{children}</>;
}

// Determine if we should use hash routing as fallback
// Use hash routing in production to prevent 404s on page refresh
const shouldUseHashRouter = () => {
  if (typeof window !== 'undefined') {
    // Check if we're in production
    if (window.location.hostname !== 'localhost' && 
        window.location.hostname !== '127.0.0.1') {
      // Check if this is a refresh that resulted in a 404
      const refreshError = sessionStorage.getItem('refresh_error');
      if (refreshError) {
        return true;
      }
      
      // If URL has a path and it's not the initial load, use hash router
      if (window.location.pathname !== '/' && 
          window.location.pathname !== '/index.html') {
        return true;
      }
    }
  }
  return false;
};

// Component to handle 404 detection
function NotFoundHandler() {
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Mark that we're refreshing
      sessionStorage.setItem('refreshing', 'true');
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // If we're refreshing and get a 404, mark error for next load
    if (sessionStorage.getItem('refreshing') === 'true') {
      sessionStorage.removeItem('refreshing');
      sessionStorage.setItem('refresh_error', 'true');
    }
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  
  return null;
}

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Index />} />
    <Route path="/select-company" element={<SelectCompany />} />
    <Route path="/user-details" element={<UserDetails />} />
    <Route path="/upload-resume" element={<UploadResume />} />
    <Route path="/interview" element={<Interview />} />
    <Route path="*" element={<NotFound />} />
  </Routes>
);

const App = () => {
  const [useHashRouter, setUseHashRouter] = useState(false);
  
  useEffect(() => {
    // Check if we should use hash router after component mounts
    setUseHashRouter(shouldUseHashRouter());
  }, []);
  
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <NotFoundHandler />
          {useHashRouter ? (
            // Use hash router as fallback
            <HashRouter>
              <MicrophoneGuard>
                <AppRoutes />
              </MicrophoneGuard>
            </HashRouter>
          ) : (
            // Use browser router as primary
            <BrowserRouter>
              <MicrophoneGuard>
                <AppRoutes />
              </MicrophoneGuard>
            </BrowserRouter>
          )}
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;